/**
 * <featured-collection-v2> — self-contained product slider (RYZ-161).
 *
 * Wraps the scroll-snap track (<ul data-track>) and the segmented progress
 * indicator (<div data-indicator>). The prev/next buttons live in the section
 * header (siblings of this element) and are found via the shared section root
 * (`.featured-collection-v2`).
 *
 * Advance mode (data-advance):
 *   - "item" (default): arrows move one product; indicator has one segment per slide.
 *   - "view":           arrows move one full view (N columns); indicator has one
 *                       segment per page (ceil(slides / columns visible)).
 *
 * Deliberately NOT built on the shared `slider-component` (global.js), whose
 * constructor queries `.slideshow__slide` and never finds generic slides.
 * Native CSS scroll-snap does the swiping; this class only wires the buttons,
 * the indicator, and the active state.
 */
(function () {
  class FeaturedCollectionV2 extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.indicator = this.querySelector('[data-indicator]');
      if (!this.track) return;

      this.slides = Array.from(this.track.querySelectorAll('[data-slide]'));
      if (this.slides.length === 0) return;
      this.visibleSlides = this.slides;

      this.advance = this.dataset.advance === 'view' ? 'view' : 'item';

      const root = this.closest('.featured-collection-v2');
      this.prevBtn = root && root.querySelector('[data-prev]');
      this.nextBtn = root && root.querySelector('[data-next]');
      this.nav = root && root.querySelector('.featured-collection-v2__nav');
      this.noResults = this.querySelector('[data-no-results]');

      this.measure();

      // one AbortController removes every listener at once on disconnect
      this._ac = new AbortController();
      const signal = this._ac.signal;

      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollByStep(-1), { signal });
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollByStep(1), { signal });

      // cross-slider page filter (dispatched by the filter bar section)
      document.addEventListener('fcv2:filter', (e) => this.applyFilter(e.detail && e.detail.filters), { signal });

      this.initDrag(signal);
      this.initScrollHint(signal);

      this._resizeObserver = new ResizeObserver(() => this.measure());
      this._resizeObserver.observe(this.track);

      // measure once more after layout/fonts settle
      requestAnimationFrame(() => this.measure());
    }

    disconnectedCallback() {
      if (this._ac) this._ac.abort();
      if (this._resizeObserver) this._resizeObserver.disconnect();
      if (this._io) this._io.disconnect();
      if (this._raf) cancelAnimationFrame(this._raf);
    }

    measure() {
      const slides = this.visibleSlides;
      if (!slides || slides.length === 0) return;

      // step width = distance between two consecutive slides (includes the gap)
      if (slides.length > 1) {
        this.slideWidth = slides[1].offsetLeft - slides[0].offsetLeft;
      } else {
        this.slideWidth = slides[0].getBoundingClientRect().width;
      }
      if (!this.slideWidth || this.slideWidth < 1) this.slideWidth = this.track.clientWidth;

      // how many full slides fit in the viewport (the "view")
      this.perView = Math.max(1, Math.round(this.track.clientWidth / this.slideWidth));

      if (this.advance === 'view') {
        this.step = this.perView;
        this.segmentCount = Math.ceil(slides.length / this.perView);
      } else {
        this.step = 1;
        this.segmentCount = slides.length;
      }

      this.buildIndicator(this.segmentCount);
      this.update();
    }

    // Cross-slider page filter. `filters` is a map of { dataKey: value }; a slide
    // matches when, for every dimension with a non-empty value, its `data-{key}`
    // (a comma list) includes that value. Empty value = no constraint for that key.
    applyFilter(filters) {
      filters = filters || {};
      this.track.classList.add('fcv2-filtering');
      this.slides.forEach((slide) => {
        let match = true;
        for (const key in filters) {
          const val = (filters[key] || '').trim().toLowerCase();
          if (!val) continue;
          const values = (slide.dataset[key] || '').toLowerCase().split(',').map((s) => s.trim());
          if (values.indexOf(val) === -1) { match = false; break; }
        }
        slide.classList.toggle('fcv2-hidden', !match);
      });

      this.visibleSlides = this.slides.filter((s) => !s.classList.contains('fcv2-hidden'));
      const empty = this.visibleSlides.length === 0;

      if (this.noResults) this.noResults.hidden = !empty;
      this.track.hidden = empty;
      if (this.indicator) this.indicator.hidden = empty;
      if (this.nav) this.nav.toggleAttribute('disabled', empty);
      if (empty) return;
      setTimeout(() => this.track.classList.remove('fcv2-filtering'), 500);

      this.track.scrollLeft = 0;
      this.measure();
    }

    buildIndicator(total) {
      if (!this.indicator) return;
      if (this.indicator.childElementCount !== total) {
        this.indicator.innerHTML = '';
        this.indicator.setAttribute('aria-label', `${total} slides`);
        this.indicator.dataset.total = total;
        for (let i = 0; i < total; i++) {
          const seg = document.createElement('span');
          seg.className = 'featured-collection-v2__indicator-segment';
          this.indicator.appendChild(seg);
        }
        this._lastIndex = -1; // force setActive to re-apply against the new segments
      }
    }

    onScroll() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this.update();
      });
    }

    update() {
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;

      // distance covered per step (one item or one full view)
      const unit = this.step * this.slideWidth;
      let index = Math.round(this.track.scrollLeft / unit);
      const maxIndex = this.segmentCount - 1;
      if (atEnd) index = maxIndex;
      if (index < 0) index = 0;
      if (index > maxIndex) index = maxIndex;

      this.currentIndex = index;
      if (index !== this._lastIndex) {
        this._lastIndex = index;
        this.setActive(index);
      }

      if (this.prevBtn) this.prevBtn.disabled = atStart;
      if (this.nextBtn) this.nextBtn.disabled = atEnd;

      // hide the whole nav when there's nothing to scroll (both buttons disabled)
      if (this.nav) this.nav.toggleAttribute('disabled', atStart && atEnd);
    }

    setActive(index) {
      if (!this.indicator) return;
      const segments = this.indicator.children;
      for (let i = 0; i < segments.length; i++) {
        segments[i].classList.toggle('is-active', i === index);
      }
    }

    scrollByStep(direction) {
      // Navigate by absolute page/item index (not relative scrollBy): the last
      // page is clamped to maxScroll, so relative stepping would desync from the
      // indicator grid. Scroll to `target * unit`, clamped, and guarantee movement.
      const unit = this.step * this.slideWidth;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const maxIndex = this.segmentCount - 1;
      const EPS = 2;

      let target = (this.currentIndex || 0) + direction;
      if (target < 0) target = 0;
      if (target > maxIndex) target = maxIndex;

      let left = Math.min(target * unit, maxScroll);

      // If the clamped target equals the current position (can happen at the
      // clamped last page), keep moving one index further so the click registers.
      if (direction < 0) {
        while (target > 0 && left >= this.track.scrollLeft - EPS) {
          target -= 1;
          left = Math.min(target * unit, maxScroll);
        }
      } else {
        while (target < maxIndex && left <= this.track.scrollLeft + EPS) {
          target += 1;
          left = Math.min(target * unit, maxScroll);
        }
      }

      this.track.scrollTo({ left, behavior: 'smooth' });
    }

    // Mouse drag-to-swipe (desktop). Touch/pen already scroll natively, so those
    // pointer types are ignored to avoid fighting the browser's own gesture.
    initDrag(signal) {
      const track = this.track;
      let isDown = false;
      let startX = 0;
      let startScroll = 0;
      let moved = false;
      let pointerId = null;

      // A card's <a>/<img> is natively draggable — grabbing one starts a
      // drag-and-drop that fires pointercancel and kills our scroll drag. Cancel
      // the native drag so the whole track drags instead.
      track.addEventListener('dragstart', (e) => e.preventDefault(), { signal });

      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        isDown = true;
        moved = false;
        startX = e.clientX;
        startScroll = track.scrollLeft;
        pointerId = e.pointerId;
        this._userInteracted = true;
        track.classList.add('is-dragging');
      }, { signal });

      track.addEventListener('pointermove', (e) => {
        if (!isDown) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 3 && !moved) {
          moved = true;
          // Only capture once an actual drag starts: capturing on every
          // pointerdown retargets the resulting click event to `track`
          // (per the Pointer Capture spec), which silently swallowed clicks
          // on card links even when the pointer never moved.
          try { track.setPointerCapture(pointerId); } catch (_) {}
        }
        if (moved && e.cancelable) e.preventDefault();
        track.scrollLeft = startScroll - dx;
      }, { signal });

      const end = (e) => {
        if (!isDown) return;
        isDown = false;
        track.classList.remove('is-dragging');
        try { track.releasePointerCapture(e.pointerId); } catch (_) {}
        if (moved) {
          this._suppressClick = true;
          this.snapToNearest();
        }
      };
      track.addEventListener('pointerup', end, { signal });
      track.addEventListener('pointercancel', end, { signal });

      // swallow the click that follows a drag so a card link isn't triggered
      track.addEventListener('click', (e) => {
        if (this._suppressClick) {
          e.preventDefault();
          e.stopPropagation();
          this._suppressClick = false;
        }
      }, { capture: true, signal });
    }

    // After a free drag, snap to the nearest item boundary (finest grid).
    snapToNearest() {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      let left = Math.round(this.track.scrollLeft / this.slideWidth) * this.slideWidth;
      if (left < 0) left = 0;
      if (left > maxScroll) left = maxScroll;
      this.track.scrollTo({ left, behavior: 'smooth' });
    }

    // Once the slider scrolls into view, nudge it ~50px and back to hint that it
    // is slideable. Skips when reduced-motion is set, when there's nothing to
    // scroll, or once the user has already interacted.
    initScrollHint(signal) {
      if (typeof IntersectionObserver === 'undefined') return;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const markInteracted = () => { this._userInteracted = true; };
      this.track.addEventListener('wheel', markInteracted, { passive: true, once: true, signal });
      this.track.addEventListener('touchstart', markInteracted, { passive: true, once: true, signal });

      this._io = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          const maxScroll = this.track.scrollWidth - this.track.clientWidth;
          if (maxScroll < 60 || this.track.scrollLeft > 2 || this._userInteracted) return;
          this.hintNudge();
        });
      }, { threshold: 0.4 });
      this._io.observe(this);
    }

    hintNudge() {
      // ~a third of a slide; snap is disabled during the hint so the partial
      // scroll actually animates instead of being snapped back immediately.
      const dist = Math.max(40, Math.round(this.slideWidth / 3));
      this.track.classList.add('is-hinting');
      this.track.scrollTo({ left: dist, behavior: 'smooth' });
      window.setTimeout(() => {
        if (!this._userInteracted) this.track.scrollTo({ left: 0, behavior: 'smooth' });
        window.setTimeout(() => this.track.classList.remove('is-hinting'), 500);
      }, 500);
    }
  }

  if (!customElements.get('featured-collection-v2')) {
    customElements.define('featured-collection-v2', FeaturedCollectionV2);
  }
})();
